import {
  AlertCircle,
  CheckCircle,
  Eye,
  FileUp,
  Trash2,
  XCircle,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog'
import { StatusBadge } from '@/components/status-badge'
import { PropertyDocuments } from '@/models/property-documents'
import { Status } from '@/models/status'
import { PropertyDocumentStatusFormValues } from '@/schemas/property-document-status-schema'
import api from '@/services/api'

import { Button } from './button'
import { ErrorField } from './error-field'
import { ScrollArea } from './scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip'

interface UploadDocumentsProps {
  status?: string | null
  uploadedFiles: PropertyDocuments[]
  setUploadedFiles: React.Dispatch<React.SetStateAction<PropertyDocuments[]>>
  contratos: number | null
  isAdmin: boolean
  isTheOwnerOfTheProperty: boolean
}

export function UploadDocuments({
  uploadedFiles,
  setUploadedFiles,
  contratos,
  isAdmin,
  isTheOwnerOfTheProperty,
}: UploadDocumentsProps) {
  const { control, formState, handleSubmit, setValue } =
    useForm<PropertyDocumentStatusFormValues>()
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [isStatusModalOpen, setIsStatusModalOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [documentStatusOptions, setDocumentStatusOptions] = useState<Status[]>(
    [],
  )

  const documentIcons = {
    1: <AlertCircle className="text-alert-warning size-5 md:size-6" />,
    2: <CheckCircle className="text-alert-success size-5 md:size-6" />,
    3: <XCircle className="text-alert-danger size-5 md:size-6" />,
  }

  useEffect(() => {
    if (!isAdmin) return
    async function getDocumentStatusOptions() {
      await api
        .get<Status[]>('/document/status')
        .then((response) => setDocumentStatusOptions(response.data))
        .catch((error) => toast.error(error.response?.data?.message))
    }

    getDocumentStatusOptions()
  }, [isAdmin])

  async function handleDocumentStatusChange(
    statusData: PropertyDocumentStatusFormValues,
  ) {
    const backObject = {
      id_status: statusData.status,
    }

    await api
      .put(`/document/status/${selectedFileId}`, backObject)
      .then(({ data }) => {
        setUploadedFiles((prevFiles) =>
          prevFiles.map((file) =>
            file.id_doc === selectedFileId
              ? {
                  ...file,
                  status: Number(statusData.status) as typeof file.status,
                }
              : file,
          ),
        )

        toast.success(data)
      })
      .catch((error) => {
        toast.error(error.response?.data?.message)
      })
      .finally(() => setIsStatusModalOpen(false))
  }

  const maxUploadDocuments = Number(
    import.meta.env.VITE_MAX_PROPERTY_UPLOAD_LIMIT ??
      window.env.MAX_PROPERTY_UPLOAD_LIMIT,
  )

  const handleFileChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0]
      const validTypes = ['image/jpeg', 'image/png', 'application/pdf']
      const fileSizeMB = Number(import.meta.env.VITE_MAX_PROPERTY_FILE_SIZE_MB)

      const fileSize = Number.isNaN(fileSizeMB)
        ? window.env.MAX_PROPERTY_FILE_SIZE_MB
        : fileSizeMB

      const maxSize = fileSize * 1024 * 1024

      if (!validTypes.includes(file.type)) {
        return toast.error('Selecione um arquivo do tipo (jpeg, png) ou PDF')
      }
      if (file.size > maxSize) {
        return toast.error(`Arquivo maior que o permitido de (${fileSize}MB)`)
      }
      setUploadedFiles((prevFiles) =>
        prevFiles.map((f, i) => (i === index ? { ...f, file } : f)),
      )
      event.target.value = ''
    }
  }

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.map((f, i) => (i === index ? { ...f, file: null } : f)),
    )
  }

  const handleDelete = async () => {
    if (selectedFileId) {
      setLoading(true)
      await api
        .delete(`/document/${selectedFileId}`)
        .then(() => {
          toast.success('Documento excluído com sucesso!')
          setUploadedFiles((prevFiles) => {
            const updatedFiles = prevFiles.filter(
              (file) => file.id_doc !== selectedFileId,
            )
            // Preenche a lista com até 15 itens novamente
            return [
              ...updatedFiles,
              ...Array(maxUploadDocuments - updatedFiles.length).fill({
                nome_original: '',
                id_doc: '',
                url_obj: '',
                file: null,
                url: '',
                isFromBackend: false,
              }),
            ]
          })
        })
        .catch((error) => {
          toast.error(error.response.data.message ?? error.message)
        })
        .finally(() => {
          setIsDeleteModalOpen(false)
          setLoading(false)
        })
    }
  }

  return (
    <div>
      <ScrollArea className="pr-3">
        <div className="flex h-96 flex-col gap-2">
          {uploadedFiles.map((file, index) => (
            <label
              key={index}
              htmlFor={
                isAdmin && !isTheOwnerOfTheProperty && file.id_doc
                  ? ''
                  : `documentsUpload${index}`
              }
            >
              <div
                onClick={() => {
                  if (isAdmin && isTheOwnerOfTheProperty && file.id_doc) {
                    toast.warning(
                      'Apenas outros administradores podem alterar o status dos documentos',
                    )

                    return
                  }

                  if (isAdmin && !isTheOwnerOfTheProperty && file.id_doc) {
                    setSelectedFileId(file.id_doc)
                    setValue('status', String(file.status) ?? '') // Mantém status como string
                    setIsStatusModalOpen(true)
                  }
                }}
                className={twMerge(
                  'flex w-full flex-col items-center justify-between gap-2 rounded-lg',
                  'hover:border-bios-300 cursor-pointer border bg-white p-3 transition-all hover:shadow-lg sm:flex-row md:gap-0',
                  file.url_obj &&
                    !isAdmin &&
                    !isTheOwnerOfTheProperty &&
                    'cursor-default',
                )}
                data-testid="upload-document-label"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    {file.status && documentIcons[file.status]}
                  </div>

                  <div
                    className={twMerge(
                      'text-monochromatic-400 max-w-52 truncate md:max-w-92 2xl:max-w-160',
                      !file.isFromBackend && 'md:max-w-140 2xl:max-w-200',
                    )}
                  >
                    {file.nome_original || file.file?.name ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span data-testid="upload-document-name">
                              {file.nome_original || file.file?.name}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {file.nome_original || file.file?.name}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span data-testid="upload-document-name">
                        Faça o upload do arquivo
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-5 md:gap-8">
                  {file.status && <StatusBadge status={file.status} />}
                  {file.file ? (
                    ((isAdmin && !isTheOwnerOfTheProperty) ||
                      !file.url ||
                      contratos === null ||
                      contratos === 0) && (
                      <Trash2
                        className="text-monochromatic-400 size-5 cursor-pointer md:size-7"
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          if (file.id_doc) {
                            setSelectedFileId(file.id_doc)
                            setIsDeleteModalOpen(true)
                          } else if (file.file !== null) {
                            handleRemoveFile(index)
                          }
                        }}
                        data-testid="upload-document-trash"
                      />
                    )
                  ) : (
                    <FileUp className="text-monochromatic-400 size-5 md:size-7" />
                  )}

                  {file.url && (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="upload-document-link"
                    >
                      <Eye className="text-monochromatic-400 size-5 md:size-7" />
                    </a>
                  )}
                </div>
                {!file.url_obj && (
                  <input
                    id={`documentsUpload${index}`}
                    className="hidden"
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => handleFileChange(index, e)}
                    data-testid="upload-document-hidden-input"
                  />
                )}
              </div>
            </label>
          ))}
        </div>
      </ScrollArea>

      {isAdmin && !isTheOwnerOfTheProperty && selectedFileId !== null && (
        <Dialog open={isStatusModalOpen} onOpenChange={setIsStatusModalOpen}>
          <DialogContent
            className="h-auto lg:max-w-[30vw] 2xl:max-w-[25vw]"
            data-testid="upload-document-status-modal"
          >
            <DialogHeader className="gap-2">
              <DialogTitle>Status da documentação</DialogTitle>
              <DialogDescription>
                Altere o status do documento selecionado.
              </DialogDescription>
            </DialogHeader>

            <form
              id="document_status_form"
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleSubmit(handleDocumentStatusChange)(e)
              }}
              data-testid="upload-document-status-form"
            >
              <Controller
                name="status"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={String(value)}
                    onValueChange={(e) => onChange(e)}
                    data-testid="upload-document-status-select"
                  >
                    <SelectTrigger
                      id="id_status"
                      withError={!!formState.errors.status}
                      data-testid="upload-document-status-select-trigger"
                    >
                      <SelectValue
                        placeholder="Selecione o status"
                        data-testid="upload-document-status-select-value"
                      />
                    </SelectTrigger>

                    <SelectContent className="z-50">
                      {documentStatusOptions.map(
                        ({ descricao, id_status: id }) => (
                          <SelectItem
                            key={id}
                            value={id.toString()}
                            data-testid="upload-document-status-select-item"
                          >
                            {descricao}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>

                    {formState.errors.status && (
                      <ErrorField>{formState.errors.status.message}</ErrorField>
                    )}
                  </Select>
                )}
              />

              <DialogFooter className="mt-4">
                <Button
                  disabled={formState.isSubmitting}
                  className="w-full"
                  type="submit"
                  isSubmitting={formState.isSubmitting}
                  data-testid="upload-document-status-save-button"
                >
                  Salvar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent
          className="h-auto max-w-96"
          data-testid="upload-document-delete-modal"
        >
          <DialogHeader>
            <DialogTitle>Excluir o documento?</DialogTitle>
            <DialogDescription>
              Tem certeza que quer excluir o documento? Essa ação é
              irreversível.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <DialogClose asChild>
              <Button
                variant="tertiary"
                className="w-full"
                data-testid="upload-document-delete-cancel-button"
              >
                Cancelar
              </Button>
            </DialogClose>

            <Button
              className="w-full"
              onClick={handleDelete}
              disabled={loading}
              data-testid="upload-document-delete-button"
              isSubmitting={loading}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
